import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Likert } from "@/components/Likert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t, LangToggle } from "@/lib/i18n";
import { VolunteerAffiliation } from "@/components/VolunteerAffiliation";
import { useStudioTheme, resolvePublicTheme } from "@/lib/theme";
import { DtMark } from "@/components/DtMark";

const StudioMark = () => (
  <div className="studio-only mt-8" aria-hidden="true">
    <div className="flex gap-2">
      {[["D", "#B4E500"], ["I", "#EFA4C5"], ["S", "#A9D4F5"], ["C", "#FFFEF9"]].map(([k, c]) => (
        <span key={k} className="h-14 w-14 rounded-xl border border-[#181818] flex items-center justify-center font-['Archivo'] font-black text-2xl text-[#181818]" style={{ background: c }}>{k}</span>
      ))}
    </div>
  </div>
);

const PAGE = 6;

const Intro = ({ church, info, setInfo, onStart, lang, setLang }) => (
  <div className="fade-up">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <p className="eyebrow">{church.name} · {t(lang, "assessment")}</p>
      {church.pro && <LangToggle lang={lang} onChange={setLang} />}
    </div>
    <h1 className="font-display text-navy text-4xl sm:text-5xl font-semibold tracking-tight mt-4 leading-tight">
      {t(lang, "discover")} <span className="font-accent text-teal">{t(lang, "wired")}</span>
    </h1>
    <StudioMark />
    <p className="text-muted-ink text-lg leading-relaxed mt-5" data-testid="assess-intro">
      {church.free ? (lang === "es" ? "Unos 5 minutos. Evaluación gratuita de personalidad DISC: 24 afirmaciones. No hay respuestas correctas o incorrectas." : "About 5 minutes. A free DISC personality assessment: 24 statements. There are no right or wrong answers, so answer as you really are.") : t(lang, "intro")}
      {church.intro?.[lang] && <span className="block mt-3 text-base" data-testid="assess-intro-context">{lang === "es" ? `Tus resultados te mostrarán dónde encajas mejor en ${church.intro.es}.` : `Your results will show where you fit best in ${church.intro.en}.`}</span>}
    </p>
    <form className="card-soft assess-form p-8 mt-10 space-y-5" onSubmit={(e) => { e.preventDefault(); onStart(); }}>
      <div className="space-y-2">
        <Label>{t(lang, "fullName")}</Label>
        <Input required value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} className="h-12" data-testid="assess-name-input" />
      </div>
      <div className="space-y-2">
        <Label>{t(lang, "email")}</Label>
        <Input required type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} className="h-12" data-testid="assess-email-input" />
      </div>
      {church.pco && <div className="space-y-2">
        <Label>{t(lang, "phone")}</Label>
        <Input type="tel" maxLength={30} value={info.phone || ""} onChange={(e) => setInfo({ ...info, phone: e.target.value })} className="h-12" data-testid="assess-phone-input" />
      </div>}
      {!church.free && <VolunteerAffiliation info={info} setInfo={setInfo} lang={lang} />}
      {!church.free && <p className="text-xs text-muted-ink leading-relaxed" data-testid="assess-share-notice">
        {t(lang, church.pco ? "shareNoticePco" : "shareNotice", { church: church.name })}
      </p>}
      <button className="btn-navy w-full" data-testid="assess-begin-btn">{t(lang, "begin")}</button>
    </form>
  </div>
);

export default function Assessment({ free = false }) {
  const { slug: routeSlug } = useParams();
  const slug = free ? "free" : routeSlug;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const lang = params.get("lang") === "es" ? "es" : "en";
  const setLang = (l) => setParams((p) => { p.set("lang", l); return p; }, { replace: true });
  const [church, setChurch] = useState(null);
  const [qs, setQs] = useState(null);
  const [err, setErr] = useState(false);
  const key = `dt-assess-${slug}`;
  const saved = JSON.parse(sessionStorage.getItem(key) || "{}");
  const [info, setInfo] = useState(saved.info || { name: "", email: "", share: true });
  const [page, setPage] = useState(saved.page ?? -1);
  const [disc, setDisc] = useState(saved.disc || {});
  const [gifts, setGifts] = useState(saved.gifts || {});
  const [busy, setBusy] = useState(false);
  useStudioTheme(!free && !!church && resolvePublicTheme(params, church.theme) === "studio");

  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify({ info, page, disc, gifts }));
  }, [key, info, page, disc, gifts]);

  useEffect(() => {
    if (free) setChurch({ name: "Dream Teams", active: true, free: true, pro: true });
    else api.get(`/public/church/${slug}`).then((r) => setChurch(r.data)).catch(() => setErr(true));
  }, [slug, free]);

  useEffect(() => {
    api.get("/public/questions", { params: { lang } }).then((r) => setQs(r.data));
  }, [lang]);

  const pages = useMemo(() => {
    if (!qs) return [];
    const out = [];
    for (let i = 0; i < qs.disc.length; i += PAGE) out.push({ kind: "disc", items: qs.disc.slice(i, i + PAGE) });
    if (!free) for (let i = 0; i < qs.gifts.length; i += PAGE) out.push({ kind: "gifts", items: qs.gifts.slice(i, i + PAGE) });
    return out;
  }, [qs, free]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-navy font-display text-2xl" data-testid="assess-not-found">{t(lang, "notActive")}</div>;
  if (!church || !qs) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-teal" /></div>;

  if (!church.active) return <div className="min-h-screen flex items-center justify-center px-6 text-center text-navy font-display text-2xl" data-testid="assess-paused">{t(lang, "paused")}</div>;

  const cur = pages[page];
  const answers = cur?.kind === "disc" ? disc : gifts;
  const setAnswers = cur?.kind === "disc" ? setDisc : setGifts;
  const complete = cur?.items.every((q) => answers[q.i]);
  const progress = page < 0 ? 0 : Math.round((page / pages.length) * 100);

  const next = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (page < pages.length - 1) return setPage(page + 1);
    setBusy(true);
    try {
      const discAnswers = qs.disc.map((q) => disc[q.i]);
      const { data } = free
        ? await api.post("/public/free/submit", { name: info.name, email: info.email, lang, disc_answers: discAnswers })
        : await api.post(`/public/church/${slug}/submit`, {
          ...info, affiliation: info.affiliation || null, share: true, pco_consent: !!church.pco, lang, disc_answers: discAnswers, gift_answers: qs.gifts.map((q) => gifts[q.i]),
        });
      sessionStorage.removeItem(key);
      navigate(`/r/${data.volunteer_id}?lang=${lang}${params.get("preview") ? `&preview=${params.get("preview")}` : ""}`);
    } catch {
      toast.error(t(lang, "submitError"));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="assess-header sticky top-0 z-20 bg-white border-b border-line">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between text-sm">
          <span className="flex items-center gap-3 font-display text-navy font-semibold min-w-0">{church.logo_file_id && <img src={mediaUrl(church.logo_file_id)} alt="" className="h-8 w-auto" data-testid="assess-church-logo" />}{!free && <span className="truncate">{church.name}</span>}</span>
          <span className="flex items-center gap-3 sm:gap-4 shrink-0">
            {page >= 0 && <span className="text-muted-ink whitespace-nowrap text-xs sm:text-sm" data-testid="assess-progress-label">{cur.kind === "disc" ? t(lang, "personality") : t(lang, "gifts")} · {progress}%</span>}
            <DtMark />
          </span>
        </div>
        <div className="assess-progress h-1 bg-teal-soft"><div className="h-1 bg-teal transition-[width] duration-500" style={{ width: `${progress}%` }} data-testid="assess-progress-bar" /></div>
      </div>
      <main className="studio-main assess-main max-w-2xl mx-auto px-6 py-14">
        {page < 0 ? (
          <Intro church={church} info={info} setInfo={setInfo} onStart={() => setPage(0)} lang={lang} setLang={setLang} />
        ) : (
          <div key={page}>
            <p className="eyebrow">{cur.kind === "disc" ? t(lang, "part1") : t(lang, "part2")}</p>
            <h2 className="font-display text-navy text-2xl font-semibold mt-2 mb-8">{t(lang, "howMuch")}</h2>
            <div className="space-y-4">
              {cur.items.map((q) => (
                <Likert key={`${cur.kind}-${q.i}`} text={q.text} value={answers[q.i]}
                  onChange={(n) => setAnswers({ ...answers, [q.i]: n })} testid={`likert-${cur.kind}-${q.i}`} labels={t(lang, "likert")} />
              ))}
            </div>
            <div className="flex justify-between mt-10">
              <button className="btn-ghost" onClick={() => setPage(page - 1)} data-testid="assess-back-btn">{t(lang, "back")}</button>
              <button className="btn-navy" disabled={!complete || busy} onClick={next} data-testid="assess-next-btn">
                {busy ? t(lang, "scoring") : page === pages.length - 1 ? t(lang, "seeResults") : t(lang, "next")}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
